import { useCallback } from 'react';
import { sanitizeNestedBlockquotes } from '@sdk/components/richText/helpers/sanitize';

import { useNavigate } from '@web/Router/router.hooks';
import { usePostMessage } from '@web/conversations/hooks/usePostMessage';
import {
  useConversationActions,
  useConversationState,
} from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { useNavigateToMessageAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useNavigateToMessageAction';
import { MessagePost } from '@web/conversations/types/Message';

type ID = number;
type Props = {
  conversationId: ID;
  message: MessagePost;
  isRetrying?: boolean;
};

export const usePostMessageAction = () => {
  const { addMessage, addMessageFail, addMessageSuccess, unselectQuotedMessage } =
    useConversationActions();
  const { selectedMessageId } = useConversationState();
  const { navigateToMessageId } = useNavigateToMessageAction();
  const navigate = useNavigate();
  const mutation = usePostMessage();

  const { mutateAsync } = mutation;

  const action = useCallback(
    async (payload: Props) => {
      // @INFO if selectedMessageId has a value, then conversation is in
      // search/browse history messages mode and should be reset
      const shouldRedirectToParentConversation = !!selectedMessageId;

      if (!payload.isRetrying && !shouldRedirectToParentConversation) {
        addMessage({
          message: {
            ...payload.message,
            content: sanitizeNestedBlockquotes(payload.message.content),
          },
        });
        unselectQuotedMessage();
      }

      try {
        const newMessage = await mutateAsync({
          attachments: payload.message.attachments,
          content: payload.message.content,
          conversationId: payload.message.conversationId,
          parentMessage: payload.message.parentMessage
            ? {
                id: payload.message.parentMessage.id,
              }
            : undefined,
        });

        if (!shouldRedirectToParentConversation) {
          addMessageSuccess({
            message: newMessage,
            tmpMessageId: payload.message.id,
          });
          navigateToMessageId(newMessage.id);
        }
      } catch {
        if (!shouldRedirectToParentConversation) {
          addMessageFail({
            messageId: payload.message.id,
          });
        }
      } finally {
        if (shouldRedirectToParentConversation) {
          navigate.toConversationDetails({
            params: {
              id: payload.conversationId,
            },
          });
        }
      }
    },
    [
      addMessage,
      addMessageFail,
      addMessageSuccess,
      mutateAsync,
      navigate,
      navigateToMessageId,
      selectedMessageId,
      unselectQuotedMessage,
    ]
  );

  return { action, mutation };
};
